import React from "react";
import { Link } from "react-router-dom";
import AnimatedBackground from "@/components/AnimatedBackground";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import {
    Sparkles,
    Users,
    Wallet,
    Receipt,
    ShieldCheck,
    Building2,
    ArrowRight,
    Check,
} from "lucide-react";

const CURRENCY_LINE = [
    { c: "TRY", s: "₺", l: "Türk Lirası" },
    { c: "USD", s: "$", l: "Dolar" },
    { c: "EUR", s: "€", l: "Euro" },
    { c: "GBP", s: "£", l: "Sterlin" },
];

export default function Landing() {
    return (
        <div className="dk-landing">
            <AnimatedBackground />

            {/* Nav */}
            <nav className="relative z-10 max-w-7xl mx-auto flex items-center justify-between px-6 py-5">
                <div
                    className="flex items-center gap-2"
                    data-testid="brand-logo"
                >
                    <Logo size={36} />
                    <span className="font-bold text-lg tracking-tight text-slate-900">
                        DentDefter
                    </span>
                </div>
                <div className="flex items-center gap-3">
                    <Link to="/login">
                        <Button
                            variant="ghost"
                            data-testid="nav-login-btn"
                            className="text-slate-700"
                        >
                            Giriş Yap
                        </Button>
                    </Link>
                    <Link to="/login">
                        <Button
                            className="dk-btn-primary rounded-full px-6"
                            data-testid="nav-cta-btn"
                        >
                            Kliniğim İçin Başla
                        </Button>
                    </Link>
                </div>
            </nav>

            {/* Hero */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 pt-16 pb-24">
                <div className="max-w-3xl">
                    <span className="dk-fade-up inline-flex items-center gap-2 rounded-full bg-white/70 backdrop-blur px-4 py-1.5 text-xs font-medium tracking-wide text-[#0F4C5C] border border-teal-100">
                        <Sparkles className="w-3.5 h-3.5" />
                        DİŞ KLİNİKLERİ İÇİN MODERN ÖN MUHASEBE
                    </span>
                    <h1 className="dk-fade-up dk-fade-up-2 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight mt-6 text-slate-900 leading-[1.05]">
                        Hastalarınızı, ödemelerinizi ve giderlerinizi{" "}
                        <span className="bg-gradient-to-r from-[#0F4C5C] to-[#00A896] bg-clip-text text-transparent">
                            tek panelden
                        </span>{" "}
                        yönetin
                    </h1>
                    <p className="dk-fade-up dk-fade-up-3 text-lg text-slate-600 mt-6 leading-relaxed max-w-2xl">
                        TL, Dolar, Euro ve Sterlin destekli tedavi anlaşmaları,
                        ön ödeme + taksit takibi ve günlük gider kaydı. Süper
                        admin panelinden birden fazla kliniği tek merkezden
                        yönetin.
                    </p>
                    <div className="dk-fade-up dk-fade-up-4 flex flex-wrap items-center gap-4 mt-9">
                        <Link to="/login">
                            <Button
                                size="lg"
                                className="dk-btn-primary rounded-full px-8 h-12 text-base"
                                data-testid="hero-cta-btn"
                            >
                                Hemen Başla
                                <ArrowRight className="w-4 h-4 ml-2" />
                            </Button>
                        </Link>
                        <div className="flex items-center gap-2 text-sm text-slate-600">
                            <ShieldCheck className="w-4 h-4 text-teal-600" />
                            Klinik başına izole veri • 3 seviyeli yetki
                        </div>
                    </div>
                </div>

                {/* Currency ticker */}
                <div className="dk-fade-up dk-fade-up-4 mt-16 overflow-hidden rounded-2xl dk-glass py-4">
                    <div className="dk-currency-ticker whitespace-nowrap px-6 text-slate-700">
                        {[...CURRENCY_LINE, ...CURRENCY_LINE, ...CURRENCY_LINE].map(
                            (c, i) => (
                                <span
                                    key={i}
                                    className="flex items-center gap-2 text-base"
                                >
                                    <span className="text-2xl font-bold text-[#0F4C5C]">
                                        {c.s}
                                    </span>
                                    <span className="font-semibold">
                                        {c.c}
                                    </span>
                                    <span className="text-slate-500">
                                        {c.l}
                                    </span>
                                    <span className="mx-6 text-teal-500">
                                        •
                                    </span>
                                </span>
                            ),
                        )}
                    </div>
                </div>
            </section>

            {/* Features */}
            <section className="relative z-10 max-w-7xl mx-auto px-6 py-20">
                <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 max-w-2xl">
                    Bir klinik için gereken her şey, doğru sırayla.
                </h2>
                <div className="grid md:grid-cols-3 gap-6 mt-12">
                    {[
                        {
                            icon: Users,
                            title: "Hasta Kayıt Sistemi",
                            desc: "Detaylı hasta profilleri, iletişim bilgileri ve tedavi geçmişi tek yerde.",
                        },
                        {
                            icon: Wallet,
                            title: "Çoklu Para Birimi",
                            desc: "TL, USD, EUR, GBP ile anlaşma yapın. Anlaşma anındaki kur sabit tutulur.",
                        },
                        {
                            icon: Receipt,
                            title: "Ön Ödeme & Taksit",
                            desc: "Ön ödeme ve parça parça ödeme kayıtlarını dakikalar içinde alın.",
                        },
                        {
                            icon: Building2,
                            title: "Süper Admin Paneli",
                            desc: "Birden fazla klinik için patron hesapları açın, hepsini merkezden yönetin.",
                        },
                        {
                            icon: ShieldCheck,
                            title: "3 Seviyeli Yetki",
                            desc: "Süper admin, klinik patronu ve personel için ayrı ekranlar ve yetkiler.",
                        },
                        {
                            icon: Sparkles,
                            title: "Günlük Gider Takibi",
                            desc: "Kira, personel, sarf malzeme, laboratuvar giderlerini kategori bazlı takip edin.",
                        },
                    ].map((f, i) => (
                        <div
                            key={i}
                            className="dk-glass rounded-2xl p-6 hover:-translate-y-1 transition-transform duration-300"
                        >
                            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#0F4C5C] to-[#00A896] flex items-center justify-center text-white mb-4">
                                <f.icon className="w-5 h-5" />
                            </div>
                            <h3 className="text-xl font-semibold text-slate-900">
                                {f.title}
                            </h3>
                            <p className="text-slate-600 mt-2 leading-relaxed">
                                {f.desc}
                            </p>
                        </div>
                    ))}
                </div>
            </section>

            {/* Pricing tease */}
            <section className="relative z-10 max-w-4xl mx-auto px-6 py-24 text-center">
                <div className="dk-glass rounded-3xl p-10 sm:p-14">
                    <h2 className="text-3xl sm:text-4xl font-bold text-slate-900">
                        Kliniğinizi bugün dijitalleştirin
                    </h2>
                    <p className="text-slate-600 mt-4 max-w-xl mx-auto">
                        Süper admin size özel bir patron hesabı açar, siz de
                        personelinizi ekleyip klinik operasyonunuzu yönetmeye
                        başlarsınız.
                    </p>
                    <ul className="mt-8 space-y-3 text-left max-w-md mx-auto">
                        {[
                            "Sınırsız hasta ve tedavi kaydı",
                            "TL, USD, EUR, GBP anlaşmalar",
                            "Ön ödeme + parçalı ödeme takibi",
                            "Günlük gider yönetimi ve raporlama",
                        ].map((t, i) => (
                            <li
                                key={i}
                                className="flex items-center gap-3 text-slate-700"
                            >
                                <span className="w-6 h-6 rounded-full bg-teal-100 flex items-center justify-center">
                                    <Check className="w-3.5 h-3.5 text-teal-700" />
                                </span>
                                {t}
                            </li>
                        ))}
                    </ul>
                    <Link to="/login" className="inline-block mt-10">
                        <Button
                            size="lg"
                            className="dk-btn-primary rounded-full px-10 h-12"
                            data-testid="footer-cta-btn"
                        >
                            Giriş Yap ve Kullan
                        </Button>
                    </Link>
                </div>
            </section>

            <footer className="relative z-10 max-w-7xl mx-auto px-6 py-10 text-center text-sm text-slate-500 border-t border-slate-200/60">
                © {new Date().getFullYear()} DentDefter • Diş klinikleri için
                ön muhasebe SaaS platformu
            </footer>
        </div>
    );
}
